import { addDoc, collection, getFirestore } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { _NEW_LINE, _SPACE_CARD, firebaseConfig } from '../components/constants';
import { addOrUpdateWord, getWordField, logParamsToFirebase } from './firedb';
import { delay } from '../components/foo';
import _ from 'lodash';
import { getLabelRootName } from './tree';
import { getBase64FromURL } from './images';

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export type ImageSourceType = 'ARASAAC' | 'Sclera' | 'Flickr30k' | 'Unsplash' | 'Custom';
type ServerType = 'Firestore' | 'Origin' | 'RP';

const DEFAULT_TIMEOUT = 5000;

/**
 * Checks if a word is valid.
 * @param word The word to check.
 */
function isValidWord(word: string): boolean {
    word = word.trim().replace(_NEW_LINE, "");
    word = word.replace(_SPACE_CARD, "");

    if(word === "") return false;
    return true;
}

/**
 * Fetches images from Firestore based on a GitHubId.
 */
async function fetchImagesFromGitHub(gitHubIds: number[], maximumImages: number, source: ImageSourceType): Promise<string[] | null> {
    // Fetch images from GitHub using the GitHubIds up to the maximumImages limit
    const images: string[] = [];
    for (let i = 0; i < Math.min(maximumImages, gitHubIds.length); i++) {
        const gitHubId = gitHubIds[i];
        const URL = `https://twrblog.github.io/dsapp-media/${source}/${gitHubId}.png`;
        // Get the base64 image from the URL
        const base64Image = await getBase64FromURL(URL);
        images.push(base64Image);
    }
    if(images.length === 0) return null;
    return images;
}


/**
 * Fetches images directly from the ARASAAC or Unsplash APIs.
 */
async function fetchImagesFromOrigin(lang: string, word: string, source: ImageSourceType, maximumImages: number): Promise<string[] | null> {
    if (!word) return null;

    try {
        if (source === "ARASAAC") {
            const response = await fetchWithTimeout(`https://api.arasaac.org/api/pictograms/${lang}/search/${encodeURIComponent(word)}`, undefined, DEFAULT_TIMEOUT);
            if (!response) return null;
            const data = await response.json();
            const results: string[] = [];
            for (let i = 0; i < Math.min(maximumImages, data.length); i++) {
                const imageId = data[i]._id;
                const imageResponse = await fetchWithTimeout(`https://static.arasaac.org/pictograms/${imageId}/${imageId}_500.png`, undefined, DEFAULT_TIMEOUT);
                if (!imageResponse) return null;
                try {
                    const blob = await imageResponse.blob();
                    const image = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.readAsDataURL(blob);
                    });
                    results.push(image);
                } catch (error) {
                    console.error(error);
                }
                // Assuming delay function is defined somewhere
                await delay(100); 
            }
            
            if (results.length === 0) {
                return null;
            }
            return results;
        } else if (source === "Unsplash") {
            const endpoint = `https://api.unsplash.com/search/photos?query=${encodeURIComponent(word)}&lang=${encodeURIComponent(lang)}&per_page=${maximumImages}`;
            const response = await fetchWithTimeout(endpoint, {
                headers: {
                    'Authorization': 'Client-ID RAGHahnxtiEyS1hWZd_fQ1WotWzX79nLLrkRrSHYgSQ',
                },
            }, DEFAULT_TIMEOUT);
            if (!response) return null;
            const data = await response.json();
            const images = await Promise.all(
                data.results.map(async (image: any) => {
                    const response = await fetchWithTimeout(image.urls.regular, undefined, DEFAULT_TIMEOUT);
                    if (!response) return null;
                    const blob = await response.blob();
                    return new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.readAsDataURL(blob);
                    });
                })
            );
            return images;
        }
    } catch (error) {
        console.error(error);
        return null;
    }
    return null; // This line is technically unreachable but included for completeness
}

/**
 * Fetches images from the RP server.
 */
async function fetchImagesFromRP(word: string, source: ImageSourceType, maximumImages: number): Promise<string[] | null> {
    // Stub implementation. Replace with actual server request.
    return null;
}

/**
 * Normalizes words before processing.
 */
function normalizeWord(word: string): string {
    return getLabelRootName(word).toLowerCase().trim();
}

/**
 * Gets a GitHubId for a word from Firestore.
 * Example of how Firestore should be structured:
 * "words" (collection) -> word: string (document) -> ARASAAC:[id1, id2, id3] (field with array of numbers)
 */
async function getGitHubIdFromFirestore(word: string, source: ImageSourceType): Promise<number[] | null> {
    return getWordField(word, source).catch(() => null);
}

/**
 * Main function to fetch images based on various parameters, with additional logging.
 */
export async function fetchImages(lang: string, word: string, maximumImages: number, source: ImageSourceType, server: ServerType, logToFB: boolean, logParamsToFB: boolean): Promise<string[] | null> {
    if (logParamsToFB) {
        await logParamsToFirebase("fetchImages", { lang, word, maximumImages, source, server }).catch(() => null);
    }
  
    const normalizedWord = normalizeWord(word);

    if (!isValidWord(normalizedWord)) return null;
  
    if (logToFB) {
        await addOrUpdateWord(normalizedWord).catch(() => null);
    }
  
    try {
        switch (server) {
            case 'Firestore':
                const gitHubIds = await getGitHubIdFromFirestore(normalizedWord, source).catch(() => null);
                if (gitHubIds !== null && (source === 'ARASAAC' || source === 'Unsplash')) {
                    return await fetchImagesFromGitHub(gitHubIds, maximumImages, source).catch(() => null);
                }
                break;
            case 'Origin':
                if (source === 'ARASAAC' || source === 'Unsplash') {
                    return await fetchImagesFromOrigin(lang, normalizedWord, source, maximumImages).catch(() => null);
                }
                break;
            case 'RP':
                return await fetchImagesFromRP(normalizedWord, source, maximumImages).catch(() => null);
            default:
                return null;
        }
    } catch {
        return null;
    }
  
    return null;
}

/**
 * Wrapper function to provide a fallback mechanism for server selection.
 */
export async function fetchImagesWithFallback(lang: string, word: string, maximumImages: number, source: ImageSourceType, logToFB: boolean, logParamsToFB: boolean): Promise<string[] | null> {
    if (!isValidWord(normalizeWord(word))) return null;
    let images = await fetchImages(lang, word, maximumImages, source, 'Firestore', logToFB, logParamsToFB).catch(() => null);
    if (!images) {
        images = await fetchImages(lang, word, maximumImages, source, 'Origin', logToFB, logParamsToFB).catch(() => null);
    }
    return images;
}

/**
 * Fetches a URL with a timeout.
 */
async function fetchWithTimeout(url: string, options: RequestInit | undefined, timeout: number): Promise<Response | null> {
    let timeoutHandle: NodeJS.Timeout;
    const timeoutPromise = new Promise<null>((resolve) => {
        timeoutHandle = setTimeout(() => resolve(null), timeout);
    });

    const fetchPromise = fetch(url, options).then(response => {
        clearTimeout(timeoutHandle);
        return response;
    });

    const result = await Promise.race([fetchPromise, timeoutPromise]);
    return result;
}



